import React, { useRef, useEffect } from 'react';
import './PDFView.css';

/** Pull data:image URL from strings that include a label prefix or whitespace in base64. */
function extractDataImageUrlFromString(value) {
  if (typeof value !== 'string') return null;
  const idx = value.search(/data:image\/(png|jpeg|jpg|webp);base64,/i);
  if (idx < 0) return null;
  const fromData = value.slice(idx);
  const m = fromData.match(/^data:image\/(png|jpeg|jpg|webp);base64,/i);
  if (!m) return null;
  const prefix = m[0];
  const rest = fromData.slice(prefix.length);
  const b64 = rest.replace(/\s/g, '');
  if (b64.length < 40) return null;
  return `${prefix}${b64}`;
}

/** Data URL or wrapped signature object → img src, or null */
function getSignatureImageSrc(value) {
  if (value == null) return null;
  if (typeof value === 'object' && typeof value.image === 'string' && value.image.trim()) {
    const s = value.image.trim();
    return extractDataImageUrlFromString(s) || (s.startsWith('data:image/') ? s : null);
  }
  if (typeof value === 'string' && value.trim()) {
    const t = value.trim();
    return extractDataImageUrlFromString(t) || (t.startsWith('data:image/') ? t : null);
  }
  return null;
}

function fieldKeyLooksLikeSignature(key) {
  return /signature|_sign$|_sign_/i.test(String(key));
}

const PDFView = ({ form, formDefinition, formValues, onClose }) => {
  const printRef = useRef(null);

  useEffect(() => {
    // Scroll to top when PDF view opens
    if (printRef.current) {
      printRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Render form field value in print-friendly format
  const renderFieldValue = (field, value) => {
    if (field.type === 'signature') {
      if (value && typeof value === 'object' && value.image) {
        return (
          <div className="signature-field">
            <img src={value.image} alt="Signature" className="signature-image" />
          </div>
        );
      } else if (value && typeof value === 'string' && value.trim()) {
        return (
          <div className="signature-field">
            <img src={value} alt="Signature" className="signature-image" />
          </div>
        );
      } else {
        return <span className="field-empty">[Not signed]</span>;
      }
    } else if (field.type === 'checkbox') {
      if (Array.isArray(value) && value.length > 0) {
        return <span>{value.join(', ')}</span>;
      } else {
        return <span className="field-empty">[Not selected]</span>;
      }
    } else if (field.type === 'date') {
      return <span>{value || <span className="field-empty">[Not provided]</span>}</span>;
    } else if (field.type === 'textarea') {
      return (
        <div className="field-textarea-value">
          {value || <span className="field-empty">[Not provided]</span>}
        </div>
      );
    } else if (field.type === 'select' || field.type === 'radio') {
      return <span>{value || <span className="field-empty">[Not selected]</span>}</span>;
    } else {
      const sigSrc = getSignatureImageSrc(value);
      if (sigSrc) {
        return (
          <div className="signature-field">
            <img src={sigSrc} alt="Signature" className="signature-image" />
          </div>
        );
      }
      return <span>{value || <span className="field-empty">[Not provided]</span>}</span>;
    }
  };

  // Render form sections and fields
  const renderFormContent = () => {
    // Handle PDF forms that don't have a form definition
    if (!formDefinition || !formDefinition.sections) {
      // Check if this is a PDF form with stored data
      if (formValues && Object.keys(formValues).length > 0) {
        return (
          <div className="form-section">
            <div className="form-section-title">Submitted Data</div>
            <div className="form-section-note">
              This is a PDF form submission. The data below was collected from the interactive PDF form.
            </div>
            {Object.entries(formValues).map(([key, value]) => {
              // Skip internal fields
              if (key === 'student_intake_id' || key === 'form_type' || key === 'submitted_at') return null;

              const sigSrc = getSignatureImageSrc(value);

              return (
                <div key={key} className="form-field">
                  <div className="form-field-label">
                    {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </div>
                  <div className="form-field-value">
                    {sigSrc ? (
                      <div className="signature-field">
                        <img src={sigSrc} alt="Signature" className="signature-image" />
                      </div>
                    ) : (
                      renderFieldValue(
                        { type: fieldKeyLooksLikeSignature(key) ? 'signature' : 'text' },
                        value
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      }
      return <div>Loading form...</div>;
    }

    return formDefinition.sections.map((section, sectionIndex) => {
      if (section.admin_only || section.is_form_header) return null;

      return (
        <div key={sectionIndex} className="form-section">
          {section.section_name && (
            <div className="form-section-title">{section.section_name}</div>
          )}
          {section.section_note && (
            <div className="form-section-note">{section.section_note}</div>
          )}
          {section.fields.map((field, fieldIndex) => {
            if (field.admin_only) return null;

            // Check visibility for conditional fields
            let isVisible = true;
            if (field.conditional) {
              const conditionalField = field.conditional.field;
              const conditionalValue = field.conditional.value;
              const currentValue = formValues[conditionalField];
              isVisible = Array.isArray(conditionalValue)
                ? conditionalValue.includes(currentValue)
                : conditionalValue === currentValue;
            }

            if (!isVisible) return null;

            const value = formValues[field.name];

            return (
              <div key={fieldIndex} className="form-field">
                <div className="form-field-label">
                  {field.label}
                  {field.required && <span className="required-asterisk"> *</span>}
                </div>
                <div className="form-field-value">
                  {renderFieldValue(field, value)}
                </div>
              </div>
            );
          })}
        </div>
      );
    });
  };

  const handlePrint = () => {
    // Create a new window with the form content for printing
    const printWindow = window.open('', '_blank');
    const formHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${form.instance_name || form.master_form_name}</title>
          <style>
            @media print {
              @page {
                margin: 1in;
              }
              body { margin: 0; }
              .no-print { display: none !important; }
            }
            body { 
              font-family: Arial, sans-serif; 
              padding: 2rem; 
              margin: 0;
            }
            .pdf-header {
              border-bottom: 2px solid #000;
              padding-bottom: 1rem;
              margin-bottom: 2rem;
            }
            .pdf-header h1 {
              margin: 0 0 0.5rem 0;
              font-size: 1.5rem;
            }
            .pdf-meta {
              color: #666;
              font-size: 0.9rem;
              margin-top: 0.5rem;
            }
            .pdf-meta div {
              margin-bottom: 0.25rem;
            }
            .pdf-content {
              line-height: 1.6;
            }
            .form-section {
              margin-bottom: 2rem;
              page-break-inside: avoid;
            }
            .form-section-title {
              font-size: 1.2rem;
              font-weight: bold;
              margin-bottom: 1rem;
              border-bottom: 1px solid #ccc;
              padding-bottom: 0.5rem;
            }
            .form-section-note {
              margin-bottom: 1rem;
              font-style: italic;
              color: #666;
              font-size: 0.95rem;
            }
            .form-field {
              margin-bottom: 1.25rem;
              page-break-inside: avoid;
            }
            .form-field-label {
              font-weight: bold;
              margin-bottom: 0.5rem;
              color: #333;
            }
            .form-field-value {
              margin-left: 1rem;
              min-height: 1.5rem;
              color: #000;
            }
            .field-empty {
              color: #999;
              font-style: italic;
            }
            .field-textarea-value {
              white-space: pre-wrap;
              margin-left: 1rem;
              padding: 0.5rem;
              background: #f9f9f9;
              border: 1px solid #ddd;
              border-radius: 4px;
              min-height: 2rem;
            }
            .signature-field {
              margin-top: 1rem;
              margin-bottom: 1rem;
            }
            .signature-image {
              max-width: 300px;
              border: 1px solid #000;
              display: block;
            }
            .no-print {
              text-align: center;
              margin-top: 2rem;
              padding: 1rem;
              background: #f5f5f5;
              border-top: 1px solid #ccc;
            }
            .no-print button {
              padding: 0.75rem 2rem;
              font-size: 1rem;
              margin: 0 0.5rem;
              border: none;
              border-radius: 4px;
              cursor: pointer;
            }
            .btn-print {
              background: #3498db;
              color: white;
            }
          </style>
        </head>
        <body>
          <div class="pdf-header">
            <h1>${form.instance_name || form.master_form_name}</h1>
            <div class="pdf-meta">
              <div><strong>Student:</strong> ${form.student_first_name} ${form.student_last_name}</div>
              <div><strong>Submitted:</strong> ${form.submitted_at ? new Date(form.submitted_at).toLocaleString() : new Date(form.created_at).toLocaleString()}</div>
              ${form.parent_first_name ? `<div><strong>Parent:</strong> ${form.parent_first_name} ${form.parent_last_name}</div>` : ''}
            </div>
          </div>
          <div class="pdf-content" id="form-content">
            <!-- Form content will be rendered here -->
          </div>
          <div class="no-print">
            <button class="btn-print" onclick="window.print()">Print</button>
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.write(formHTML);
    printWindow.document.close();
    
    // Render form content
    setTimeout(() => {
      renderFormToPDF(printWindow.document.getElementById('form-content'), formDefinition, formValues);
      // Auto-trigger print dialog after content is loaded
      setTimeout(() => {
        printWindow.print();
      }, 250);
    }, 100);
  };

  const handleDownload = () => {
    // Create a new window with the form content for printing/downloading
    const printWindow = window.open('', '_blank');
    const formHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${form.instance_name || form.master_form_name}</title>
          <style>
            @media print {
              @page {
                margin: 1in;
              }
              body { margin: 0; }
              .no-print { display: none !important; }
            }
            body { 
              font-family: Arial, sans-serif; 
              padding: 2rem; 
              margin: 0;
            }
            .pdf-header {
              border-bottom: 2px solid #000;
              padding-bottom: 1rem;
              margin-bottom: 2rem;
            }
            .pdf-header h1 {
              margin: 0 0 0.5rem 0;
              font-size: 1.5rem;
            }
            .pdf-meta {
              color: #666;
              font-size: 0.9rem;
              margin-top: 0.5rem;
            }
            .pdf-content {
              line-height: 1.6;
            }
            .form-section {
              margin-bottom: 2rem;
              page-break-inside: avoid;
            }
            .form-section-title {
              font-size: 1.2rem;
              font-weight: bold;
              margin-bottom: 1rem;
              border-bottom: 1px solid #ccc;
              padding-bottom: 0.5rem;
            }
            .form-field {
              margin-bottom: 1rem;
            }
            .form-field-label {
              font-weight: bold;
              margin-bottom: 0.25rem;
            }
            .form-field-value {
              margin-left: 1rem;
              min-height: 1.5rem;
            }
            .form-field-value img {
              max-width: 100%;
              height: auto;
              border: 1px solid #ccc;
            }
            .signature-field {
              margin-top: 2rem;
              margin-bottom: 2rem;
            }
            .signature-image {
              max-width: 300px;
              border: 1px solid #000;
            }
            .no-print {
              text-align: center;
              margin-top: 2rem;
              padding: 1rem;
              background: #f5f5f5;
              border-top: 1px solid #ccc;
            }
            .no-print button {
              padding: 0.75rem 2rem;
              font-size: 1rem;
              margin: 0 0.5rem;
              border: none;
              border-radius: 4px;
              cursor: pointer;
            }
            .btn-print {
              background: #3498db;
              color: white;
            }
            .btn-download {
              background: #27ae60;
              color: white;
            }
            .btn-close {
              background: #95a5a6;
              color: white;
            }
          </style>
        </head>
        <body>
          <div class="pdf-header">
            <h1>${form.instance_name || form.master_form_name}</h1>
            <div class="pdf-meta">
              <div><strong>Student:</strong> ${form.student_first_name} ${form.student_last_name}</div>
              <div><strong>Submitted:</strong> ${form.submitted_at ? new Date(form.submitted_at).toLocaleString() : new Date(form.created_at).toLocaleString()}</div>
              ${form.parent_first_name ? `<div><strong>Parent:</strong> ${form.parent_first_name} ${form.parent_last_name}</div>` : ''}
            </div>
          </div>
          <div class="pdf-content" id="form-content">
            <!-- Form content will be rendered here -->
          </div>
          <div class="no-print">
            <button class="btn-print" onclick="window.print()">Print</button>
            <button class="btn-download" onclick="window.print()">Download as PDF</button>
            <button class="btn-close" onclick="window.close()">Close</button>
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.write(formHTML);
    printWindow.document.close();
    
    // Render form content
    setTimeout(() => {
      renderFormToPDF(printWindow.document.getElementById('form-content'), formDefinition, formValues);
    }, 100);
  };

  const renderFormToPDF = (container, formDefinition, formValues) => {
    // Handle PDF forms that don't have a form definition
    if (!formDefinition || !formDefinition.sections) {
      if (formValues && Object.keys(formValues).length > 0) {
        const sectionDiv = document.createElement('div');
        sectionDiv.className = 'form-section';

        const titleDiv = document.createElement('div');
        titleDiv.className = 'form-section-title';
        titleDiv.textContent = 'Submitted Data';
        sectionDiv.appendChild(titleDiv);

        const noteDiv = document.createElement('div');
        noteDiv.style.marginBottom = '1rem';
        noteDiv.style.fontStyle = 'italic';
        noteDiv.style.color = '#666';
        noteDiv.textContent = 'This is a PDF form submission. The data below was collected from the interactive PDF form.';
        sectionDiv.appendChild(noteDiv);

        Object.entries(formValues).forEach(([key, value]) => {
          // Skip internal fields
          if (key === 'student_intake_id' || key === 'form_type' || key === 'submitted_at') return;

          const fieldDiv = document.createElement('div');
          fieldDiv.className = 'form-field';

          const labelDiv = document.createElement('div');
          labelDiv.className = 'form-field-label';
          labelDiv.textContent = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          fieldDiv.appendChild(labelDiv);

          const valueDiv = document.createElement('div');
          valueDiv.className = 'form-field-value';
          const sigSrc = getSignatureImageSrc(value);
          if (sigSrc) {
            const img = document.createElement('img');
            img.src = sigSrc;
            img.className = 'signature-image';
            img.alt = 'Signature';
            valueDiv.appendChild(img);
          } else if (value && typeof value === 'object' && !Array.isArray(value)) {
            valueDiv.textContent = fieldKeyLooksLikeSignature(key) ? '[Not signed]' : '[Not provided]';
          } else {
            valueDiv.textContent = value != null && value !== '' ? String(value) : '[Not provided]';
          }
          fieldDiv.appendChild(valueDiv);

          sectionDiv.appendChild(fieldDiv);
        });

        container.appendChild(sectionDiv);
      }
      return;
    }

    formDefinition.sections.forEach(section => {
      if (section.admin_only || section.is_form_header) return;

      const sectionDiv = document.createElement('div');
      sectionDiv.className = 'form-section';

      if (section.section_name) {
        const titleDiv = document.createElement('div');
        titleDiv.className = 'form-section-title';
        titleDiv.textContent = section.section_name;
        sectionDiv.appendChild(titleDiv);
      }

      if (section.section_note) {
        const noteDiv = document.createElement('div');
        noteDiv.style.marginBottom = '1rem';
        noteDiv.style.fontStyle = 'italic';
        noteDiv.style.color = '#666';
        noteDiv.textContent = section.section_note;
        sectionDiv.appendChild(noteDiv);
      }

      section.fields.forEach(field => {
        if (field.admin_only) return;

        const fieldDiv = document.createElement('div');
        fieldDiv.className = 'form-field';

        const labelDiv = document.createElement('div');
        labelDiv.className = 'form-field-label';
        labelDiv.textContent = field.label + (field.required ? ' *' : '');
        fieldDiv.appendChild(labelDiv);

        const valueDiv = document.createElement('div');
        valueDiv.className = 'form-field-value';

        const value = formValues[field.name];

        if (field.type === 'signature') {
          if (value && typeof value === 'object' && value.image) {
            const img = document.createElement('img');
            img.src = value.image;
            img.className = 'signature-image';
            img.alt = 'Signature';
            valueDiv.appendChild(img);
          } else if (value && typeof value === 'string' && value.trim()) {
            const img = document.createElement('img');
            img.src = value;
            img.className = 'signature-image';
            img.alt = 'Signature';
            valueDiv.appendChild(img);
          } else {
            valueDiv.textContent = '[Not signed]';
          }
        } else if (field.type === 'checkbox') {
          if (Array.isArray(value) && value.length > 0) {
            valueDiv.textContent = value.join(', ');
          } else {
            valueDiv.textContent = '[Not selected]';
          }
        } else if (field.type === 'date') {
          valueDiv.textContent = value || '[Not provided]';
        } else if (field.type === 'textarea') {
          valueDiv.textContent = value || '[Not provided]';
          valueDiv.style.whiteSpace = 'pre-wrap';
        } else {
          valueDiv.textContent = value || '[Not provided]';
        }

        fieldDiv.appendChild(valueDiv);
        sectionDiv.appendChild(fieldDiv);
      });

      container.appendChild(sectionDiv);
    });
  };

  return (
    <div className="pdf-view-container" ref={printRef}>
      <div className="pdf-view-header no-print">
        <h3>{form.instance_name || form.master_form_name}</h3>
        <div className="pdf-view-actions">
          <button onClick={handlePrint} className="btn-primary">
            Print
          </button>
          <button onClick={handleDownload} className="btn-primary">
            Download PDF
          </button>
          <button onClick={onClose} className="btn-secondary">
            Back
          </button>
        </div>
      </div>
      <div className="pdf-view-content">
        <div className="pdf-header">
          <h1>{form.instance_name || form.master_form_name}</h1>
          <div className="pdf-meta">
            <div><strong>Student:</strong> {form.student_first_name} {form.student_last_name}</div>
            <div><strong>Submitted:</strong> {form.submitted_at ? new Date(form.submitted_at).toLocaleString() : new Date(form.created_at).toLocaleString()}</div>
            {form.parent_first_name && (
              <div><strong>Parent:</strong> {form.parent_first_name} {form.parent_last_name}</div>
            )}
          </div>
        </div>
        <div className="pdf-content">
          {renderFormContent()}
        </div>
      </div>
    </div>
  );
};

export default PDFView;

